<script setup>
import { ref, reactive, nextTick } from "vue";
import { Modal } from "bootstrap";
import { useUnitStore } from "@/stores/unit";

const emit = defineEmits(["saved"]);

const unitStore = useUnitStore();
const modalEl = ref(null);
const isEditing = ref(false);

const defaultForm = () => ({
  _id: null,
  unitCode: "",
  houseNo: "",
  building: "",
  zone: "",
  unitType: "condo",
  area: "",
  parkingSlots: 0,
  monthlyFee: "",
  occupancyStatus: "occupied",
  note: "",
});

const form = reactive(defaultForm());
const errors = reactive({
  unitCode: "",
  houseNo: "",
  building: "",
  monthlyFee: "",
});

const getModal = () => Modal.getOrCreateInstance(modalEl.value);

const resetForm = () => {
  Object.assign(form, defaultForm());
  Object.keys(errors).forEach((k) => (errors[k] = ""));
};

const validate = () => {
  errors.unitCode = form.unitCode ? "" : "กรุณากรอกรหัสยูนิต";
  errors.houseNo = form.houseNo ? "" : "กรุณากรอกบ้านเลขที่";
  errors.building = form.building ? "" : "กรุณากรอกอาคาร";
  errors.monthlyFee = Number(form.monthlyFee) > 0 ? "" : "กรุณากรอกค่าส่วนกลาง";
  return !Object.values(errors).some(Boolean);
};

const open = (unit = null) => {
  resetForm();
  if (unit) {
    isEditing.value = true;
    Object.assign(form, {
      _id: unit.id,
      unitCode: unit.unitCode,
      houseNo: unit.houseNo,
      building: unit.building,
      zone: unit.zone || "",
      unitType: unit.unitType,
      area: unit.area,
      parkingSlots: unit.parkingSlots,
      monthlyFee: unit.monthlyFee,
      occupancyStatus: unit.occupancyStatus,
      note: unit.note || "",
    });
  } else {
    isEditing.value = false;
  }
  nextTick(() => getModal().show());
};

const close = () => getModal().hide();

const handleSubmit = () => {
  if (!validate()) return;

  if (isEditing.value) {
    unitStore.updateUnit({ ...form, id: form._id });
  } else {
    unitStore.addUnit({ ...form });
  }

  close();
  emit("saved", { mode: isEditing.value ? "edit" : "add" });
};

defineExpose({ open, close });
</script>

<template>
  <div class="modal fade" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content rounded-4 border-0 shadow">
        <div class="modal-header border-0 pb-0 px-4 pt-4">
          <h5 class="modal-title fw-bold">
            <i class="bi bi-buildings me-1 text-primary"></i
            >{{ isEditing ? "แก้ไขข้อมูลยูนิต" : "เพิ่มยูนิตใหม่" }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body px-4 py-3">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                รหัสยูนิต <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.unitCode"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น A-101"
                :class="{ 'is-invalid': errors.unitCode }"
              />
              <div class="invalid-feedback">{{ errors.unitCode }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                บ้านเลขที่ <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.houseNo"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น 88/101"
                :class="{ 'is-invalid': errors.houseNo }"
              />
              <div class="invalid-feedback">{{ errors.houseNo }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                อาคาร <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.building"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น อาคาร A"
                :class="{ 'is-invalid': errors.building }"
              />
              <div class="invalid-feedback">{{ errors.building }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">โซน</label>
              <input
                v-model="form.zone"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น โซนหน้า"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">ประเภทยูนิต</label>
              <select v-model="form.unitType" class="form-select rounded-3">
                <option value="condo">ห้องชุด</option>
                <option value="house">บ้าน</option>
                <option value="shop">ร้านค้า</option>
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">พื้นที่ (ตร.ม.)</label>
              <input
                v-model.number="form.area"
                type="number"
                min="0"
                class="form-control rounded-3"
                placeholder="เช่น 35"
              />
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">ที่จอดรถ (คัน)</label>
              <input
                v-model.number="form.parkingSlots"
                type="number"
                min="0"
                class="form-control rounded-3"
                placeholder="0"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                ค่าส่วนกลาง/เดือน (บาท) <span class="text-danger">*</span>
              </label>
              <input
                v-model.number="form.monthlyFee"
                type="number"
                min="0"
                class="form-control rounded-3"
                placeholder="เช่น 1800"
                :class="{ 'is-invalid': errors.monthlyFee }"
              />
              <div class="invalid-feedback">{{ errors.monthlyFee }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">สถานะการอยู่อาศัย</label>
              <select
                v-model="form.occupancyStatus"
                class="form-select rounded-3"
              >
                <option value="occupied">มีผู้พักอาศัย</option>
                <option value="vacant">ว่าง</option>
              </select>
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">หมายเหตุ</label>
              <textarea
                v-model="form.note"
                class="form-control rounded-3"
                rows="2"
                placeholder="บันทึกข้อมูลเพิ่มเติม"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
          <button
            type="button"
            class="btn btn-danger border rounded-3 px-4"
            @click="close"
          >
            <i class="bi bi-x-lg me-1"></i>ยกเลิก
          </button>
          <button
            type="button"
            class="btn rounded-3 px-4"
            :class="isEditing ? 'btn-warning' : 'btn-success'"
            @click="handleSubmit"
          >
            <i class="bi bi-floppy2 me-1"></i
            >{{ isEditing ? "บันทึกการแก้ไข" : "เพิ่มยูนิต" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
